import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle, CheckCircle2, TrendingUp, Save, Info, Sparkles, Zap, Percent } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REGIMI = [
  { v: "forfettario", l: "Regime Forfettario" },
  { v: "ordinario", l: "Regime Ordinario" },
  { v: "dipendente", l: "Lavoratore dipendente" },
];

export default function Audit() {
  const { user } = useAuth();
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [piano, setPiano] = useState({
    regime: "forfettario",
    ricavi_previsti: 50000,
    costi_previsti: 0,
    coefficiente: 0.78,
    is_startup: false,
    auto_inps: true,
    contributi_inps: 4200,
    aliquota_iva: 22,
    reddito_lordo_previsto: 30000,
    // Fix C4.1: default 0 — la detrazione lavoro dipendente è ora calcolata automaticamente
    // dal motore (art.13 TUIR dinamica). Usa questo campo SOLO per detrazioni EXTRA
    // (interessi mutuo, spese mediche, ecc.). Metterlo a 1880 causava double-count.
    detrazioni: 0,
    note: "",
  });
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasPiano, setHasPiano] = useState(false);
  const [atecoInfo, setAtecoInfo] = useState(null); // { ateco, coefficiente, gruppo }
  const previewTimerRef = useRef(null);

  const loadReport = useCallback(async (y) => {
    try {
      const r = await api.get(`/audit/report?anno=${y}`);
      setReport(r.data);
    } catch { setReport(null); }
  }, []);

  const loadPiano = useCallback(async (y) => {
    try {
      const r = await api.get(`/audit/piano?anno=${y}`);
      if (r.data) {
        setPiano({
          regime: r.data.regime,
          ricavi_previsti: r.data.ricavi_previsti || 0,
          costi_previsti: r.data.costi_previsti || 0,
          coefficiente: r.data.coefficiente || 0.78,
          is_startup: r.data.is_startup || false,
          auto_inps: r.data.auto_inps !== false,
          contributi_inps: r.data.contributi_inps || 0,
          aliquota_iva: r.data.aliquota_iva || 22,
          reddito_lordo_previsto: r.data.reddito_lordo_previsto || 0,
          detrazioni: r.data.detrazioni || 0,
          note: r.data.note || "",
        });
        setHasPiano(true);
        loadReport(y);
      } else {
        setHasPiano(false);
        setReport(null);
      }
    } catch { setHasPiano(false); }
  }, [loadReport]);

  useEffect(() => { loadPiano(anno); }, [anno, loadPiano]);

  // Precompila regime dal profilo (solo la prima volta se piano non ancora esistente)
  useEffect(() => {
    if (!user || hasPiano) return;
    const t = user.tipo_piva;
    if (t === "forfettario" || t === "ordinario") {
      setPiano((p) => ({ ...p, regime: t }));
    } else if (t === "nessuna") {
      setPiano((p) => ({ ...p, regime: "dipendente" }));
    }
  }, [user, hasPiano]);

  // Rileva coefficiente forfettario da ATECO del profilo.
  // Se piano non esiste ancora, auto-compila il campo coefficiente.
  useEffect(() => {
    if (!user?.codice_ateco) { setAtecoInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/audit/ateco-coefficient");
        if (cancelled) return;
        setAtecoInfo(r.data);
        if (r.data?.coefficiente && !hasPiano) {
          setPiano((p) => ({ ...p, coefficiente: r.data.coefficiente }));
        }
      } catch {
        setAtecoInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.codice_ateco, hasPiano]);

  // Live preview (debounced) — chiama backend ogni volta che il form cambia
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.post("/audit/piano/preview", { anno, ...piano });
        setPreview(r.data);
      } catch (err) {
        // Silent fail: preview è chiamato on-the-fly con debounce, un errore transiente
        // non deve spammare toast. Loggo per debug in console.
        if (process.env.NODE_ENV !== "production") console.warn("Audit preview failed:", err?.message);
      }
    }, 350);
    return () => clearTimeout(previewTimerRef.current);
  }, [piano, anno]);

  const setF = (k, v) => setPiano((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/audit/piano", { anno, ...piano });
      toast.success("Piano fiscale salvato");
      setHasPiano(true);
      loadReport(anno);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const status = report?.status;
  const statusInfo = {
    ok: { color: "bg-primary/10 border-primary/30 text-primary", Icon: CheckCircle2, title: "Tutto in linea", desc: "I versamenti sono coerenti con il piano fiscale (± 5%)." },
    underpaid: { color: "bg-secondary/10 border-secondary/30 text-secondary", Icon: AlertTriangle, title: "Sotto-versato", desc: "Hai versato meno del previsto: verifica eventuali scadenze arretrate e, se serve, confrontati con il tuo commercialista." },
    overpaid: { color: "bg-blue-500/10 border-blue-500/30 text-blue-700", Icon: Info, title: "Sopra-versato", desc: "Hai versato più del previsto: potresti avere un credito d'imposta o un errore da chiarire." },
    no_data: { color: "bg-muted text-muted-foreground", Icon: Info, title: "Dati insufficienti", desc: "Aggiungi movimenti bancari con categoria 'Tasse' per il confronto." },
  }[status] || {};

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Verifica Fiscale
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Atteso vs realtà.</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Definisci il tuo <strong className="text-foreground">piano fiscale atteso</strong> per l'anno. TUOTAX lo confronta con i tuoi versamenti reali (dai movimenti bancari) e ti avvisa se qualcosa non torna.
          </p>
        </div>
        <div>
          <Label className="text-xs">Anno fiscale</Label>
          <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v))}>
            <SelectTrigger className="h-11 rounded-xl min-w-[140px]" data-testid="audit-anno"><SelectValue /></SelectTrigger>
            <SelectContent>{[2024,2025,2026,2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Status Card (only if piano exists) */}
      {report && statusInfo.Icon && (
        <Card className={`p-6 rounded-2xl border-2 ${statusInfo.color} flex items-start gap-4 fade-in-up`} data-testid="audit-status-card">
          <statusInfo.Icon className="w-8 h-8 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-xl mb-1">{statusInfo.title}</div>
            <p className="text-sm mb-4 opacity-90">{statusInfo.desc}</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Atteso {anno}</div>
                <div className="font-heading font-bold text-2xl">{euro(report.totale_atteso)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Pagato ({report.transactions_count} movimenti)</div>
                <div className="font-heading font-bold text-2xl">{euro(report.totale_pagato)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Delta</div>
                <div className="font-heading font-bold text-2xl">{report.delta >= 0 ? "" : "-"}{euro(Math.abs(report.delta))}<span className="text-sm opacity-70 ml-1">({report.delta_pct}%)</span></div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Piano form */}
        <Card className="p-6 lg:p-8 rounded-2xl border" data-testid="audit-form">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><TrendingUp className="w-5 h-5 text-primary" /></div>
            <div>
              <h2 className="font-heading font-semibold text-xl">Piano fiscale {anno}</h2>
              <p className="text-xs text-muted-foreground">Le tue previsioni di ricavi/redditi.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div><Label>Regime</Label>
              <Select value={piano.regime} onValueChange={(v) => setF("regime", v)}>
                <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="audit-regime"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIMI.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {piano.regime === "forfettario" && (
              <>
                <div><Label>Ricavi previsti (€)</Label>
                  <Input type="number" value={piano.ricavi_previsti} onChange={(e) => setF("ricavi_previsti", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-ricavi" />
                </div>
                <div><Label>Coefficiente redditività</Label>
                  <Input type="number" step="0.01" value={piano.coefficiente} onChange={(e) => setF("coefficiente", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-coeff" />
                  {atecoInfo?.coefficiente ? (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/15 p-2.5" data-testid="audit-coeff-hint">
                      <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-xs flex-1">
                        <span className="text-foreground">ATECO <strong>{atecoInfo.ateco}</strong> → coefficiente <strong>{Math.round(atecoInfo.coefficiente * 100)}%</strong></span>
                        <div className="text-muted-foreground mt-0.5">{atecoInfo.gruppo}</div>
                        {Math.abs(piano.coefficiente - atecoInfo.coefficiente) > 0.001 && (
                          <button
                            type="button"
                            onClick={() => setF("coefficiente", atecoInfo.coefficiente)}
                            className="mt-1 text-primary underline underline-offset-2 hover:text-primary/80"
                            data-testid="audit-coeff-apply-ateco"
                          >
                            Applica {Math.round(atecoInfo.coefficiente * 100)}% da ATECO
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">Dipende dal tuo ATECO. Es. professioni intellettuali 78%, commercio 40%, artigianato 67%. <Link to="/settings" className="text-primary underline">Compila l'ATECO</Link> per auto-rilevarlo.</p>
                  )}
                </div>
                <div className="flex items-center justify-between rounded-xl border p-4">
                  <Label className="font-medium">Startup (primi 5 anni, aliquota 5%)</Label>
                  <Switch checked={piano.is_startup} onCheckedChange={(v) => setF("is_startup", v)} data-testid="audit-startup" />
                </div>
                <AutoInpsToggle piano={piano} setF={setF} preview={preview} user={user} />
              </>
            )}

            {piano.regime === "ordinario" && (
              <>
                <div><Label>Ricavi previsti (€)</Label>
                  <Input type="number" value={piano.ricavi_previsti} onChange={(e) => setF("ricavi_previsti", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-ricavi" />
                </div>
                <div><Label>Costi previsti deducibili (€)</Label>
                  <Input type="number" value={piano.costi_previsti} onChange={(e) => setF("costi_previsti", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-costi" />
                  <p className="text-xs text-muted-foreground mt-1">Costi documentati (materiali, servizi, ammortamenti, ecc.)</p>
                </div>
                <div><Label>Aliquota IVA media (%)</Label>
                  <Input type="number" step="0.5" value={piano.aliquota_iva} onChange={(e) => setF("aliquota_iva", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-iva" />
                </div>
                <div><Label>Detrazioni previste (€)</Label>
                  <Input type="number" value={piano.detrazioni} onChange={(e) => setF("detrazioni", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-detr" />
                </div>
                <AutoInpsToggle piano={piano} setF={setF} preview={preview} user={user} />
              </>
            )}

            {piano.regime === "dipendente" && (
              <>
                <div><Label>Reddito lordo previsto (€)</Label>
                  <Input type="number" value={piano.reddito_lordo_previsto} onChange={(e) => setF("reddito_lordo_previsto", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-reddito" />
                </div>
                <div>
                  <Label>Detrazioni extra (€)</Label>
                  <Input type="number" value={piano.detrazioni} onChange={(e) => setF("detrazioni", parseFloat(e.target.value) || 0)} className="h-12 rounded-xl mt-1.5" data-testid="audit-detr" placeholder="0" />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Lascia 0 se non hai detrazioni oltre a quelle da lavoro dipendente (calcolate automaticamente). Aggiungi solo per: interessi mutuo, spese mediche, ecc.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground rounded-xl bg-accent/40 p-3">
                  I contributi previdenziali per il dipendente sono trattenuti direttamente in busta paga dal datore di lavoro — qui non serve inserirli.
                </p>
              </>
            )}

            <Button onClick={save} disabled={saving} className="rounded-full w-full h-12" data-testid="audit-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : hasPiano ? "Aggiorna piano" : "Calcola e salva"}
            </Button>
          </div>
        </Card>

        {/* Right column: Live preview + Breakdown */}
        <div className="space-y-6">
          {/* Live preview del calcolo */}
          {preview && (
            <Card className="p-6 lg:p-8 rounded-2xl border-2 border-primary/20 bg-primary/[0.02]" data-testid="audit-preview">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Zap className="w-5 h-5 text-primary" /></div>
                <div>
                  <h2 className="font-heading font-semibold text-xl">Anteprima calcolo</h2>
                  <p className="text-xs text-muted-foreground">Aggiornata in tempo reale mentre modifichi i valori.</p>
                </div>
              </div>

              <div className="space-y-3">
                {piano.regime === "dipendente" ? (
                  <PreviewRow label="Reddito lordo previsto" value={euro(piano.reddito_lordo_previsto)} muted />
                ) : (
                  <PreviewRow label="Ricavi previsti" value={euro(piano.ricavi_previsti)} muted />
                )}
                {piano.regime === "forfettario" && (
                  <PreviewRow label={`Reddito imponibile (${Math.round(piano.coefficiente * 100)}%)`} value={euro(preview.reddito_imponibile)} muted />
                )}
                {piano.regime === "ordinario" && piano.costi_previsti > 0 && (
                  <PreviewRow label="Costi deducibili" value={`- ${euro(piano.costi_previsti)}`} muted />
                )}

                <div className="border-t border-border/60 pt-3 space-y-2">
                  <PreviewRow
                    label="Contributi previdenziali"
                    sublabel={preview.inps_nota}
                    value={euro(preview.inps_atteso)}
                    accent="primary"
                    testid="preview-inps"
                  />
                  <PreviewRow
                    label={piano.regime === "forfettario" ? `Imposta sostitutiva (${piano.is_startup ? "5" : "15"}%)` : "IRPEF + addizionali"}
                    value={euro(preview.imposta_attesa)}
                    accent="secondary"
                    testid="preview-imposta"
                  />
                  {preview.iva_attesa > 0 && (
                    <PreviewRow
                      label={`IVA a debito (${piano.aliquota_iva}%)`}
                      sublabel="Da versare trimestralmente"
                      value={euro(preview.iva_attesa)}
                      accent="secondary"
                      testid="preview-iva"
                    />
                  )}
                </div>

                <div className="border-t border-border/60 pt-3 space-y-2">
                  <PreviewRow
                    label="Totale da versare nell'anno"
                    value={euro(preview.totale_atteso)}
                    accent="strong"
                    testid="preview-totale"
                  />
                  {piano.regime !== "dipendente" && (
                    <PreviewRow
                      label="Netto stimato"
                      sublabel="Ricavi − imposte − contributi (l'IVA è partita di giro)"
                      value={euro(preview.netto_stimato)}
                      accent="netto"
                      testid="preview-netto"
                    />
                  )}
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
                <Info className="inline w-3 h-3 mr-1 mb-0.5" />
                Stime indicative basate sulle aliquote 2026. Verifica sempre col commercialista prima di versare.
              </p>
            </Card>
          )}

          {/* Scadenze / confronto */}
          <Card className="p-6 lg:p-8 rounded-2xl border" data-testid="audit-breakdown">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary" /></div>
            <div>
              <h2 className="font-heading font-semibold text-xl">Scadenze e confronto</h2>
              <p className="text-xs text-muted-foreground">Ogni scadenza attesa vs. quanto effettivamente pagato.</p>
            </div>
          </div>

          {!report ? (
            <div className="text-muted-foreground text-sm py-12 text-center border border-dashed rounded-xl">
              Salva il piano fiscale per vedere il confronto.
            </div>
          ) : report.breakdown_matched.length === 0 ? (
            <div className="text-muted-foreground text-sm py-8 text-center">Nessuna scadenza calcolata.</div>
          ) : (
            <div className="space-y-3">
              {report.breakdown_matched.map((item, i) => {
                const overdue = new Date(item.date) < new Date() && item.pagato < item.amount * 0.95;
                const paid = item.pagato >= item.amount * 0.95;
                return (
                  <div key={i} className="p-3 rounded-xl border bg-accent/20" data-testid={`audit-item-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div>
                        <div className="text-xs text-muted-foreground">{new Date(item.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        <div className="font-semibold text-sm">{item.label}</div>
                      </div>
                      <Badge variant="outline" className={`rounded-full text-[10px] ${paid ? "bg-primary/10 text-primary border-primary/30" : overdue ? "bg-secondary/10 text-secondary border-secondary/30" : ""}`}>
                        {paid ? "Pagato" : overdue ? "In ritardo" : "In arrivo"}
                      </Badge>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <div><span className="text-muted-foreground">Atteso:</span> <strong>{euro(item.amount)}</strong></div>
                      <div><span className="text-muted-foreground">Pagato:</span> <strong className={paid ? "text-primary" : "text-secondary"}>{euro(item.pagato)}</strong></div>
                      {item.delta !== 0 && <div><span className="text-muted-foreground">Δ:</span> <strong className={item.delta > 0 ? "text-secondary" : "text-blue-700"}>{item.delta > 0 ? "-" : "+"}{euro(Math.abs(item.delta))}</strong></div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {report && report.transactions_count === 0 && (
            <div className="mt-4 p-3 rounded-xl bg-secondary/5 border border-secondary/20 text-xs text-muted-foreground flex gap-2">
              <Info className="w-4 h-4 text-secondary flex-shrink-0" />
              <div>Nessun movimento "Tasse" trovato per {anno}. <Link to="/app/movimenti" className="text-primary font-semibold">Importa il CSV bancario</Link> per vedere il confronto reale.</div>
            </div>
          )}
        </Card>
        </div>
      </div>

      <div className="text-xs text-muted-foreground pt-4">
        I calcoli sono stime basate sulle aliquote 2026 (IRPEF a scaglioni, forfettario 5%/15%, casse professionali, Gestione Separata 26,23%). Per casi complessi consulta il tuo commercialista.
      </div>
    </div>
  );
}

/* -------------------- Sub-components -------------------- */

function PreviewRow({ label, sublabel, value, accent = "default", muted = false, testid }) {
  const valueCls = {
    default: "font-heading font-semibold text-base",
    primary: "font-heading font-semibold text-base text-primary",
    secondary: "font-heading font-semibold text-base text-secondary",
    strong: "font-heading font-bold text-lg",
    netto: "font-heading font-bold text-xl text-primary",
  }[accent];
  return (
    <div className={`flex items-start justify-between gap-3 ${muted ? "opacity-70" : ""}`} data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {sublabel && <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sublabel}</div>}
      </div>
      <div className={valueCls}>{value}</div>
    </div>
  );
}

function AutoInpsToggle({ piano, setF, preview, user }) {
  const cassa = user?.cassa_previdenziale;
  const settore = user?.settore;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border p-4 bg-accent/30">
        <div className="flex-1">
          <Label className="font-medium flex items-center gap-2">
            <Percent className="w-4 h-4 text-primary" /> Calcola INPS automaticamente
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            {piano.auto_inps
              ? "Uso il tuo profilo per stimare i contributi previdenziali."
              : "Inserisco io i contributi manualmente."}
          </p>
        </div>
        <Switch
          checked={piano.auto_inps}
          onCheckedChange={(v) => setF("auto_inps", v)}
          data-testid="audit-auto-inps"
        />
      </div>

      {piano.auto_inps && preview && (
        <div className="rounded-xl bg-primary/5 border border-primary/15 p-3 flex gap-3 items-start" data-testid="audit-inps-source">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-semibold text-primary mb-0.5">
              {preview.inps_nota || "INPS calcolato dal tuo profilo"}
            </div>
            <div className="text-muted-foreground">
              Se non è la cassa giusta,{" "}
              <Link to="/app/impostazioni" className="text-primary font-semibold underline">aggiorna il tuo profilo</Link>{" "}
              {cassa || settore ? "" : "(seleziona cassa/settore)"} oppure disattiva il toggle per inserire manualmente.
            </div>
          </div>
        </div>
      )}

      {!piano.auto_inps && (
        <div>
          <Label>Contributi previdenziali previsti (€)</Label>
          <Input
            type="number"
            value={piano.contributi_inps}
            onChange={(e) => setF("contributi_inps", parseFloat(e.target.value) || 0)}
            className="h-12 rounded-xl mt-1.5"
            data-testid="audit-inps"
          />
          <p className="text-xs text-muted-foreground mt-1">Inserisci l'importo annuo totale che prevedi di versare.</p>
        </div>
      )}
    </div>
  );
}
